/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Swal from 'sweetalert2';
import Web3 from 'web3';
import { ethers } from 'ethers';
import { setLoadingAction } from '../../store/loading/loadingActions';
import * as loadingActionTypes from '../../store/loading/loadingActionTypes';
import wallet from '../../helpers/wallet';
import Spinner from 'react-bootstrap/Spinner';
import './MercurXBuyTokenModal.scss';
import { Col, Form } from 'react-bootstrap';
import { abiRequestAction } from '../../store/abi/abiActions';
import { buyTokenModalAction, getBNB } from '../../store/token/tokenActions';
import { transactionRequest } from '../../store/transaction/transactionActions';
import { mainColors } from '../../helpers/colors';
import { InputGroup } from 'react-bootstrap';
import { SiBinance } from 'react-icons/si';
import mercurxCardDefaultLogo from '../../assets/img/logo/mercurx-logo-mini.png';
import { checkUserTokenRequestAction } from '../../store/account/userActions';

function normalizeString(value) {
  return `${value ?? ''}`.trim();
}

function uniqueValues(values) {
  return [...new Set(values.filter(Boolean))];
}

function formatNumber(value) {
  return value.toLocaleString('en-US', { minimumFractionDigits: 9 });
}

function splitWords(value) {
  return normalizeString(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

function buildNameVariants(value) {
  const normalizedValue = normalizeString(value);

  if (!normalizedValue) {
    return [];
  }

  const words = splitWords(normalizedValue);
  const compact = words.join('');
  const pascalCase = words
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join('');

  return uniqueValues([
    normalizedValue,
    compact,
    pascalCase,
    normalizedValue.toUpperCase(),
    normalizedValue.toLowerCase(),
    compact.toUpperCase(),
    compact.toLowerCase(),
  ]);
}

function normalizeTypeToken(value) {
  return normalizeString(value)
    .replace(/\b(memory|calldata|storage|indexed)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseSolidityParams(paramsString) {
  const normalizedParams = normalizeString(paramsString);

  if (!normalizedParams) {
    return [];
  }

  return normalizedParams
    .split(',')
    .map((param) => normalizeTypeToken(param))
    .filter(Boolean)
    .map((param, index) => {
      const parts = param.split(' ').filter(Boolean);
      const hasNamedParam = parts.length > 1;
      const name = hasNamedParam ? parts[parts.length - 1] : `arg${index}`;
      const type = hasNamedParam ? parts.slice(0, -1).join(' ') : parts[0];

      return {
        internalType: type,
        name,
        type,
      };
    });
}

function parseStateMutability(signatureTail) {
  const normalizedTail = normalizeString(signatureTail);

  if (/\bpayable\b/.test(normalizedTail)) {
    return 'payable';
  }

  if (/\bview\b/.test(normalizedTail)) {
    return 'view';
  }

  if (/\bpure\b/.test(normalizedTail)) {
    return 'pure';
  }

  return 'nonpayable';
}

function parseFunctionsFromSoliditySource(sourceCode) {
  const normalizedSource = normalizeString(sourceCode);

  if (!normalizedSource) {
    return [];
  }

  const functions = [];
  const functionRegex = /function\s+([A-Za-z0-9_]+)\s*\(([\s\S]*?)\)\s*([^{;]*)[{;]/g;
  let match = functionRegex.exec(normalizedSource);

  while (match) {
    const [, name, paramsString, signatureTail] = match;

    functions.push({
      type: 'function',
      name,
      inputs: parseSolidityParams(paramsString),
      outputs: [],
      stateMutability: parseStateMutability(signatureTail),
    });

    match = functionRegex.exec(normalizedSource);
  }

  return functions;
}

function augmentAbiFromSource(abiData, sourceCode) {
  const parsedFunctions = parseFunctionsFromSoliditySource(sourceCode);

  if (!parsedFunctions.length) {
    return abiData;
  }

  const existingFunctionNames = new Set(
    (abiData || [])
      .filter((entry) => entry?.type === 'function')
      .map((entry) => normalizeString(entry?.name)),
  );

  const missingFunctions = parsedFunctions.filter((entry) => {
    return !existingFunctionNames.has(normalizeString(entry?.name));
  });

  if (!missingFunctions.length) {
    return abiData;
  }

  return [...abiData, ...missingFunctions];
}

function getAbiByKey(abiHistory, key) {
  const value = abiHistory?.[key];
  const abiData = Array.isArray(value)
    ? value
    : Array.isArray(value?.data)
      ? value.data
      : null;

  if (!abiData) {
    return null;
  }

  return augmentAbiFromSource(abiData, value?.code);
}

function isErc20LikeAbi(abi) {
  const methodNames = new Set(
    (abi || [])
      .filter((entry) => entry?.type === 'function')
      .map((entry) => normalizeString(entry?.name)),
  );

  return ['allowance', 'approve', 'transfer', 'transferFrom', 'decimals'].every((methodName) => {
    return methodNames.has(methodName);
  });
}

function buildTokenSaleAbiKeyCandidates(project) {
  const baseValues = [
    project?.token?.presale_contract?.abi_key,
    project?.token?.presale_contract?.contract_abi_key,
    project?.token?.presale_abi_key,
    project?.token?.sale_abi_key,
    project?.token?.symbol,
    project?.token?.name,
    project?.name,
  ];
  const suffixes = [
    '_presale_abi',
    '_sale_abi',
    'Presale_abi',
    'Sale_abi',
    '_abi',
  ];
  const keys = [];

  for (const baseValue of baseValues) {
    const variants = buildNameVariants(baseValue);
    for (const variant of variants) {
      keys.push(variant);
      for (const suffix of suffixes) {
        keys.push(`${variant}${suffix}`);
      }
    }
  }

  return uniqueValues(keys);
}

function findTokenSaleAbi(abiHistory, project) {
  const keyCandidates = buildTokenSaleAbiKeyCandidates(project);

  for (const key of keyCandidates) {
    const abi = getAbiByKey(abiHistory, key);
    if (abi) {
      return { key, abi };
    }
  }

  for (const [key, value] of Object.entries(abiHistory || {})) {
    const abi = getAbiByKey(abiHistory, key);
    if (!abi || isErc20LikeAbi(abi)) {
      continue;
    }

    const lowerKey = normalizeString(key).toLowerCase();
    const lowerCode = normalizeString(value?.code).toLowerCase();
    const methodNames = new Set(
      abi
        .filter((entry) => entry?.type === 'function')
        .map((entry) => normalizeString(entry?.name)),
    );
    const hasSaleSignal =
      lowerKey.includes('presale') ||
      lowerKey.includes('sale') ||
      methodNames.has('buyTokens') ||
      methodNames.has('buy') ||
      lowerCode.includes('function buytokens') ||
      lowerCode.includes('function buy(');

    if (hasSaleSignal) {
      return { key, abi };
    }
  }

  return { key: null, abi: null };
}

function resolveTokenSaleMethod(abi) {
  const methodNames = ['buyTokens', 'buy', 'buyToken', 'purchase'];

  return methodNames.find((methodName) => {
    return abi?.some((entry) => {
      return entry?.type === 'function' && normalizeString(entry?.name) === methodName;
    });
  }) || null;
}

function isInputValidTokenAmount(value) {
  return parseFloat(value) >= 1;
}
function isInputValid(value) {
  return parseFloat(value) > 0;
}

function BuyMerxToken({ ...props }) {
  const {
    balance_,
    signerAddress,
    token,
    setLoading,
    isLoading,
    project,
    abiHistoryRequest,
    getBNBRequest,
    abiHistory,
    buyTokenModalRequest,
    transactionRequest,
    tierData,
    bnbData,
    item,
    checkUserTokenWeb3,
  } = props;
  const [txs, setTxs] = useState([]);
  const [merxTokenInputValue, setMerxTokenInputValue] = useState(
    {
    MerxTokenAmount: 1,
    etherValue: parseFloat(bnbData?.price)/parseFloat(project?.token.price_in_usd),
  });

  useEffect(() => {
    merxTokenInputValue.MerxTokenAmount = 1;
    merxTokenInputValue.etherValue =  parseFloat(project?.token.price_in_usd)/parseFloat(bnbData?.price);
    setMerxTokenInputValue({ ...merxTokenInputValue });
  }, [bnbData]);
  const [inputError, setInputError] = useState(false);

  const MerxTokenOnChangeHandler = (event) => {
    const { name, value } = event.target;
    if (name === 'etherValue') {
      merxTokenInputValue.MerxTokenAmount = (parseFloat(bnbData?.price) / parseFloat(project?.token.price_in_usd)) * parseFloat(value) ;
      merxTokenInputValue.etherValue = value;
      setMerxTokenInputValue({ ...merxTokenInputValue });
      setInputError(!isInputValid(value));
    } else if (name === 'MerxTokenAmount') {
      merxTokenInputValue.MerxTokenAmount = value
      merxTokenInputValue.etherValue = (parseFloat(value) * parseFloat(project?.token.price_in_usd))/parseFloat(bnbData?.price);
      setMerxTokenInputValue({ ...merxTokenInputValue });
      setInputError(!isInputValidTokenAmount(value));
    }
  };

  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: 'btn btn-success',
      cancelButton: 'btn btn-danger',
    },
    buttonsStyling: false,
  });

  const closeModal = () => {
    buyTokenModalRequest(false);
  };
  const logoOnErrorHandler = (event) => {
    event.currentTarget.src = mercurxCardDefaultLogo;
    event.currentTarget.className = 'project-icon';
  };

  const handleAbi = () => {
    getBNBRequest();
    abiHistoryRequest();
  };

  useEffect(() => {
    const merxabiKey = project?.token.symbol.toUpperCase() + '_abi';
    const merxcontractAbi = abiHistory?.[merxabiKey]?.data || abiHistory?.[merxabiKey];
    if (abiHistory?.[project?.token.symbol.toUpperCase()  + '_abi']) {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const mercurx_token = new ethers.Contract(
        project?.token.address,
        merxcontractAbi,
        provider,
      );

      try {
        mercurx_token.on('Transfer', (from, to, amount, event) => {
          setTxs((currentTxs) => [
            ...currentTxs,
            {
              txHash: event?.transactionHash,
              from,
              to,
              amount: String(amount),
            },
          ]);
        });
      } catch (e) {
      }

      return () => {
        mercurx_token.removeAllListeners();
      };
    }
  }, [abiHistory, project]);

  useEffect(() => {
    handleAbi();
  }, []);
  useEffect(() => {

  }, []);
  let val = 0.0;


  const buyToken = async (e) => {
    e.preventDefault();
  
    const userToken = JSON.parse(localStorage.getItem('user'))?.token;
    if (!userToken) return;
  
    await checkUserTokenWeb3(); 
    setTxs([]);
    setLoading({ key: loadingActionTypes.BUY_MERXTOKEN_LOADING, isLoading: true });
  
    const data = new FormData(e.target);
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = await provider.getSigner();
    const signerAddress = await signer.getAddress();
    const web3 = new Web3(window.ethereum);
    await wallet.controlAndSwitchOrAddNetwork();
    await window.ethereum.enable();
  
    const saleAbiLookup = findTokenSaleAbi(abiHistory, project);
    const contractAbi = saleAbiLookup.abi;
    const contractAddress = project?.token?.presale_contract?.contract_address;
    const buyMethodName = resolveTokenSaleMethod(contractAbi);

    if (!contractAbi) {
      throw new Error(
        'Token sale ABI could not be found in the /contracts response.',
      );
    }

    if (!contractAddress) {
      throw new Error('Presale contract address is missing in the project data.');
    }

    if (!buyMethodName) {
      throw new Error(
        'A supported token purchase method could not be found in the sale contract ABI.',
      );
    }

    const mercurx_presale = new web3.eth.Contract(contractAbi, contractAddress);
  
    const etherMiktari = data.get('etherValue');
    const project_id = project?.id;
    const project_name = project?.name;
    const action = 'buy';
    const token_count = merxTokenInputValue.MerxTokenAmount * (10 ** project?.token.decimals);
    const token_address = project?.token.address;
    const transaction_time = new Date().toISOString();
    const user_public_address = signerAddress;
  
    if (project?.token.symbol === 'TRT' && tierData.tier < 1) {
      Swal.fire({
        icon: 'Error',
        iconColor: '#E40039',
        text: `Your Account Needs to be at Least Tier 1 to Swap ${project?.token.symbol} Token`,
        confirmButtonColor: '#E40039',
      }).then(closeModal);
      setLoading({ key: loadingActionTypes.BUY_MERXTOKEN_LOADING, isLoading: false });
      return;
    }
  
    try {
      let val = parseFloat(bnbData?.price) * parseFloat(etherMiktari) / parseFloat(project?.token.price_in_usd);
      val = val.toFixed(project?.token.decimals).toString();
      val = parseFloat(val.replace('.', ''), 10);
  
      let bnb = parseFloat(etherMiktari).toFixed(18);
      bnb = parseFloat(bnb.toString().replace('.', ''), 10);
  
      const transaction = await mercurx_presale.methods[buyMethodName](val).send({
        from: signerAddress,
        to: contractAddress,
        value: bnb,
      });
  
      wallet.getMyBalance(project?.token.address);
      setLoading({ key: loadingActionTypes.BUY_MERXTOKEN_LOADING, isLoading: false });
  
      const payload2 = {
        project_id,
        project_name,
        token_count,
        user_public_address,
        token_address,
        transaction_hash: transaction?.transactionHash,
        transaction_time,
        transaction_status: true,
        action,
      };
  
      transactionRequest(payload2);
  
      Swal.fire({
        icon: 'success',
        iconColor: mainColors.primary,
        text: 'Transaction succeed',
        confirmButtonColor: mainColors.primary,
        html: `<a href=https://testnet.bscscan.com/tx/${transaction?.transactionHash} target='_blank'> Check Detail Transaction !</a>`,
      }).then(closeModal);
  
    } catch (err) {
      setLoading({ key: loadingActionTypes.BUY_MERXTOKEN_LOADING, isLoading: false });

      const failedTransactionHash = err?.receipt?.transactionHash || null;
      const payload2 = {
        project_id,
        project_name,
        token_count,
        user_public_address,
        token_address,
        transaction_hash: failedTransactionHash,
        transaction_time,
        transaction_status: false,
        action,
      };
      transactionRequest(payload2);

      if (failedTransactionHash) {
        Swal.fire({
          icon: 'error',
          iconColor: '#E40039',
          title: 'Transaction is Failed',
          confirmButtonColor: '#E40039',
          html: `<a href=https://testnet.bscscan.com/tx/${failedTransactionHash} target='_blank'> Check Detail Transaction !</a>`,
        }).then(closeModal);
      } else {
        Swal.fire({
          icon: 'warning',
          iconColor: '#E40039',
          confirmButtonColor: '#E40039',
          text: err.message,
        }).then(closeModal);
      }
    }
  };
  


  return (
    <>
      <form className="m-0" onSubmit={buyToken}>
        <div
          className="credit-card w-full lg:w-3/4 sm:w-auto 
          shadow-lg mx-auto rounded-xl"
        >
          <main className="px-4">
            <h1 className="d-flex justify-content-center text-fs-head-md">
              {project?.token.symbol.toUpperCase()}/BNB
            </h1>
            <div className="mx-3">
              <div className="my-3">
                <div className="input-group">
                  <InputGroup>
                    <Form.Control
                      type="number"
                      name="etherValue"
                      id="etherValue"
                      className="input input-bordered text-fs-body-md text-t-body-color bg-light"
                      placeholder="BNB Value"
                      min="0"
                      step="any"
                      value={(merxTokenInputValue.etherValue)}
                      onChange={MerxTokenOnChangeHandler}
                      disabled={isLoading?.[loadingActionTypes.BUY_MERXTOKEN_LOADING]}
                    />
                    <InputGroup.Text id="basic-addon1" style={{ width: '95px' }}>
                      <Col className="d-flex justify-content-between align-items-center">
                        <span>BNB</span>
                        <SiBinance color="black" />
                      </Col>
                    </InputGroup.Text>
                  </InputGroup>
                  {inputError && (
                    <Form.Label className="text-danger text-fs-body-sm mercurx-error-label">
                      * The input value must be greater than 0.
                    </Form.Label>
                  )}
                </div>
              </div>
              <div className="my-3">
                <div className="input-group">
                  <InputGroup>
                    <Form.Control
                      type="number"
                      name="MerxTokenAmount"
                      id="MerxTokenAmount"
                      className="input input-bordered text-fs-body-md text-t-body-color bg-light"
                      placeholder="MerxTokenAmount"
                      value={(merxTokenInputValue.MerxTokenAmount)}
                      onChange={MerxTokenOnChangeHandler}
                      min="1"
                      step="any"
                      disabled={isLoading?.[loadingActionTypes.BUY_MERXTOKEN_LOADING]}
                    />
                    <InputGroup.Text id="basic-addon2" style={{ width: '95px' }}>
                      <Col className="d-flex justify-content-between align-items-center">
                        {project?.token.symbol.toUpperCase()}{' '}
                        <img
                          alt="token-icon"
                          src={process.env.REACT_APP_API_URL + '/projects/' + project?.id + '/logo'}
                          className="token-icon"
                          onError={logoOnErrorHandler}
                          style={{ maxWidth: '20px', maxHeight: '20px' }}
                        />
                      </Col>
                    </InputGroup.Text>
                  </InputGroup>
                  {inputError && (
                    <Form.Label className="text-danger text-fs-body-sm mercurx-error-label">
                      * The input value must be greater than 1.
                    </Form.Label>
                  )}
                  <div className="input-group-append"></div>
                </div>
              </div>
            </div>
          </main>

          <footer className="d-flex justify-content-end p-3 mx-4">
            <button
              type="submit"
              className="btn btn-primary d-flex justify-content-center"
              disabled={isLoading?.[loadingActionTypes.BUY_MERXTOKEN_LOADING] || inputError}
            >
              {' '}
              {!isLoading?.[loadingActionTypes.BUY_MERXTOKEN_LOADING] ? (
                'Buy'
              ) : (
                <div className="d-flex align-items-center justify-content-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden"></span>
                  </Spinner>
                  <span className="ml-2">Pending Transaction...</span>
                </div>
              )}
            </button>
          </footer>
        </div>
      </form>
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    provider2: state.walletReducer.provider2,
    project: state.projectReducer.project,
    signer: state.walletReducer.signer,
    signerAddress: state.walletReducer.signerAddress,
    web3: state.walletReducer.web3,
    erc20_: state.walletReducer.erc20_,
    balance_: state.walletReducer.balance_,
    contractAddress: state.walletReducer.contractAddress,
    token: state.tokenReducer.token,
    isLoading: state.loadingReducer.isLoading,
    abiHistory: state.abiReducer.abiHistory,
    tierData: state.tierReducer.tierData,
    bnbData: state.tokenReducer.bnb,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setLoading: (payload) => {
      dispatch(setLoadingAction(payload));
    },
    abiHistoryRequest: (payload) => {
      dispatch(abiRequestAction(payload));
    },
    buyTokenModalRequest: (payload) => {
      dispatch(buyTokenModalAction(payload));
    },
    getBNBRequest: (payload) => {
      dispatch(getBNB(payload));
    },
    transactionRequest: (creds) => {
      dispatch(transactionRequest(creds));
    },
    checkUserTokenWeb3: (payload) => {
      dispatch(checkUserTokenRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BuyMerxToken);
